import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { AssignmentHome, Scope } from "./AssignmentHome";
import { TraitProfile } from "./TraitProfile";
import { CollectionPeople, type Collection, type Person } from "./CollectionPeople";
import { useScoreRun } from "./ScoreRunContext";
import { RubricTable, type Awarded, type CellRef, type RubricRow } from "./RubricTable";

// The teacher's review screen: the queue on the left, one paper on the right.
//
// WHAT THIS SCREEN REFUSES TO SHOW, which is as much of the design as what it shows:
//
//  * No total, no average, no percentage. The scale is criterion-referenced — a level says the
//    writing meets that descriptor, not that it ranks anywhere — so a mean across criteria is a
//    number nobody assigned. The only counts here are counts of PAPERS in a state.
//  * A criterion the scoring could not reach shows NO number at all, not a zero and not a blank
//    cell that reads like one. "We could not tell" and "this is weak" are different findings and
//    the whole record is built to keep them apart.
//  * Evidence is only ever the student's own words, verified as an exact substring of what they
//    wrote. Spans the model proposed and could not verify are reported as a count, never as text.
//  * Prior levels from a different scoring configuration are labelled, because raw levels from two
//    raters are not directly comparable and two numbers side by side invite a trend.

type Criterion = {
  trait_id: string;
  trait_label: string | null;
  standard_code: string | null;
  scale_categories: number[] | null;
  status: string;
  level: number | null;
  confidence: string | null;
  reason: string | null;
  reason_code: string | null;
  needs_human: boolean;
  evidence: string[];
  // WHERE each verified span sits in the normalised text. The verifier returned these offsets
  // so a reader could highlight without searching again; until now they were discarded and the
  // console listed quotations beside the paper instead of showing them in it.
  spans?: { at: number; len: number; span: string | null }[];
  evidence_dropped: number;
  rubric_version: string | null;
  prior: PriorObservation[];
  /** What the rater noticed beside the score (0103) — "No counterclaim found in this paper. Scored
   *  1, the lowest level." Packets composed before 0103 have none. */
  issues?: { code: string; message: string }[];
};

type PriorObservation = {
  task_id: string | null;
  iteration: string | null;
  window_label: string | null;
  level: number | null;
  scoring_configuration_id: string | null;
  same_rater: boolean;
  when: string | null;
};

type Packet = {
  composer_version: string;
  student_id: string | null;
  section_id: string | null;
  task_id: string | null;
  iteration: string | null;
  window_label: string | null;
  text?: string;
  // The string the offsets are into, and the string the scorer was actually shown. Highlighting
  // over `text` instead would be off by however much whitespace and typography the normaliser
  // folded — silently, and by a different amount on every paper.
  text_normalized?: string;
  normalization_version?: string;
  stamp: Record<string, string | number | null>;
  traits: Criterion[];
  needs_human: string[];
  prior_rater_mismatch: boolean;
  prior_note: string | null;
  feedback?: {
    message: string;
    quotations: string[];
    composer_version: string;
    holds: { code: string; detail: string }[];
    // Set once a teacher has edited. The machine's original is kept beside it — "what the model
    // drafted" and "what the teacher sent" are two different facts, and only one of them tells you
    // how much editing the drafts actually need.
    edited_by?: string;
    machine_draft?: string;
    holds_are_advisory?: boolean;
  };
};

type QueueRow = {
  artifact_id: string;
  state: string;
  state_reason_code: string | null;
  student_id: string | null;
  section_id: string | null;
  task_id: string | null;
  iteration: string | null;
  window_label: string | null;
  display_name: string | null;
  needs_human: number | null;
  holds: number | null;
  traits: number | null;
  prior_rater_mismatch: boolean | null;
};

type ScoreEvent = {
  event_id: string;
  trait_id: string;
  status: string;
  level: number | null;
  reason: string | null;
  scorer_type: string;
  scorer_id: string | null;
  supersedes_event_id: string | null;
  current: boolean;
  created_at: string | null;
};

type Attempt = {
  delivery_id: string;
  composition_id: string;
  channel: string;
  target_ref: string | null;
  status: string;
  detail: string | null;
  message_hash: string | null;
  attempted_at: string | null;
  delivered_at: string | null;
  attempted_by: string | null;
};

type Delivery = {
  available: boolean;
  attempts: Attempt[];
  delivered: Attempt | null;
  last_attempt: Attempt | null;
};

type Transition = {
  from_state: string | null;
  to_state: string;
  actor_type: string;
  actor_id: string | null;
  created_at: string | null;
};

type IntakeFile = {
  name: string;
  status: string;
  reason_code: string | null;
  candidates: { student_id: string; display_name: string | null; score: number }[] | null;
  word_count: number | null;
  text: string | null;
  folder: string | null;
  read_at: string | null;
};

type RosterEntry = { student_id: string; display_name: string | null };

type Detail = {
  artifact_id: string;
  /** The binding, from the ARTIFACT rather than the packet. A paper that was scored and never
   *  composed has scores and no packet, and this is what says which rubric they are against. */
  task_id?: string | null;
  iteration?: string | null;
  /** The LESSON this paper was collected for (0066), and what the rubric is asked for. `iteration`
   *  is a folder name a teacher typed and identifies nothing outside its own collection — asking by
   *  it is what made every rubric cell read "no wording". Optional: a paper bound before 0066
   *  carries no lesson, and the task is the fallback. */
  resource_id?: string | null;
  composition_id: string;
  state: string;
  state_reason_code: string | null;
  student_id: string | null;
  /** Whose paper this is, by name. Null for a paper nobody is named on, where the identifier is
   *  the honest thing to show — it IS what the paper is. */
  display_name: string | null;
  intake: IntakeFile | null;
  roster: RosterEntry[];
  packet: Packet | null;
  events: ScoreEvent[];
  transitions: Transition[];
  needs_human: number;
  prior_rater_mismatch: boolean;
};

const STATE_LABEL: Record<string, string> = {
  // What HAPPENED and what is wanted, in one line. "Ready for you" said the second half only, and
  // left a teacher to work out whether anything had been scored yet.
  in_review: "Scored — ready for your review",
  bound: "Handed in — not scored yet",
  scored: "Being scored",
  composed: "Being scored",
  blocked: "Held — the draft did not clear its checks",
  released: "Released",
  withheld: "Not sent",
  not_scorable: "No attempt",
  unbound: "Could not tell whose",
};

// An outcome that carries no number is said in words by the rubric table now (RubricTable's
// NO_NUMBER), because that is where the row it belongs to is drawn.

// A name when the roster has one, the identifier when it does not. Deliberately no prettifying
// regex: a key tidied up to look like a name is worse than a key, because it hides that nobody
// knows who this is. `unbound` is a real state and it should look like one.
function who(name: string | null | undefined, id: string | null): string {
  return (name && name.trim()) || id || "—";
}



// A set is a binding key, and a paper is in it when every declared part matches. `null` in the
// scope means "not declared", which is a value a paper can genuinely have — so this compares
// rather than skipping, and a set of undeclared papers is a real set you can open.
function inScope(r: QueueRow, sc: Scope, byActivity = false): boolean {
  // A learner activity is one lesson, taught to one section, in one iteration; its window is a
  // description, not part of what it is (0045's unique key is section x lesson x iteration). So an
  // activity's papers are matched without the window — an exact match there would silently empty
  // the queue whenever the declared window was spelled differently from the activity's.
  return r.section_id === sc.section_id && r.task_id === sc.task_id
      && r.iteration === sc.iteration && (byActivity || r.window_label === sc.window_label);
}

interface Props {
  // The learner activity chosen in the sidebar, as a scope. With one, the console opens straight
  // on that activity's papers; the sidebar's picker is how you move to another.
  scope?: Scope | null;
  scopeTitle?: string;
  // The collection's own id. The scope is its binding key — enough to filter papers by, and not
  // enough to ask the server who is in the class: a roster read starts from the activity.
  activityId?: string | null;
}

export function ReviewConsole({ scope: activityScope = null, scopeTitle: activityTitle = "",
                                activityId = null }: Props = {}) {
  // Without an activity, the console opens on the sets, not on a paper. "What is waiting for me"
  // is a question the teacher asks second; "is 5B's op-ed done" is the one they arrive with.
  const byActivity = activityScope !== null;
  const [scope, setScope] = useState<Scope | null>(activityScope);
  const [scopeTitle, setScopeTitle] = useState<string>(activityTitle);
  const [atHome, setAtHome] = useState(!byActivity);
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [available, setAvailable] = useState<boolean | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The student on screen who handed nothing in. They have no paper, so `selected` cannot hold
  // them, and a screen that simply went blank would look like a click that failed.
  const [absent, setAbsent] = useState<Person | null>(null);
  // Bumped whenever this console changes a paper. The people list is a second read of the same
  // rows, and one that never re-read would go on saying "ready for you" about a paper just
  // handed back.
  const [peopleTick, setPeopleTick] = useState(0);
  const [collection, setCollection] = useState<Collection | null>(null);
  // The same shared fetch the Score control and the people list read, so refreshing it here
  // updates both rather than leaving two components with two answers.
  const { refresh: readRun } = useScoreRun();

  const loadQueue = useCallback(async () => {
    try {
      const r = await api.get<{ available: boolean; queue: QueueRow[]; counts: Record<string, number> }>(
        "/review/queue");
      setAvailable(r.available);
      setQueue(r.queue);
      setCounts(r.counts);
      // A FOLDER OPENS ON THE CLASS. With an activity chosen there is a trait profile to show,
      // and it is the first question of a set — how did they do — where a paper is the second.
      // Opening straight into whichever paper sorted first also chose a student for the teacher,
      // which is a choice nobody asked it to make.
      if (!selected && !atHome && !activityId) {
        const first = scope ? r.queue.find((x) => inScope(x, scope, byActivity)) : r.queue[0];
        if (first) setSelected(first.artifact_id);
      }
    } catch (e) {
      setAvailable(false);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [selected, atHome, scope, byActivity, activityId]);

  useEffect(() => { void loadQueue(); }, [loadQueue]);

  const changed = useCallback(async () => {
    setPeopleTick((n) => n + 1);
    // AND HOW MANY ARE WAITING TO BE SCORED. Naming a paper moves it from `unbound` into `bound`,
    // which IS the scoring queue — so the control that counts that queue is out of date the moment
    // a teacher answers "whose is this". It polls only while a run is in flight, deliberately, so
    // with no run going it had fetched once on mount and never again: twenty papers named, and the
    // button still said "Score 1 paper".
    readRun();                    // fire and forget: it sets its own state when it lands
    await loadQueue();
  }, [loadQueue, readRun]);

  function openSet(sc: Scope, title: string) {
    setScope(sc);
    setScopeTitle(title);
    setAtHome(false);
    const first = queue.find((r) => inScope(r, sc, byActivity));
    setSelected(first ? first.artifact_id : null);
  }

  // A stuck paper is opened from the home page directly, with no set around it — that is the
  // point of naming them there rather than counting them.
  function openPaper(artifact_id: string) {
    setScope(null);
    setScopeTitle("");
    setAtHome(false);
    setSelected(artifact_id);
  }

  function goHome() {
    setAtHome(true);
    setScope(null);
    setSelected(null);
    setDetail(null);
  }

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let live = true;
    setError(null);
    api.get<Detail>(`/review/artifact/${encodeURIComponent(selected)}`)
      .then((d) => { if (live) setDetail(d); })
      .catch((e) => { if (live) { setDetail(null); setError(e instanceof ApiError ? e.message : String(e)); } });
    // Separate call: a paper with no hand-back yet is the normal case, and a 404 or an empty
    // result here must not stop the review packet rendering.
    api.get<Delivery>(`/delivery/${encodeURIComponent(selected)}`)
      .then((d) => { if (live) setDelivery(d); })
      .catch(() => { if (live) setDelivery(null); });
    return () => { live = false; };
  }, [selected]);

  async function move(state: string) {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/review/${encodeURIComponent(detail.artifact_id)}/state`, { state });
      const d = await api.get<Detail>(`/review/artifact/${encodeURIComponent(detail.artifact_id)}`);
      setDetail(d);
      await changed();
    } catch (e) {
      // The database's refusal, passed through. It names the states and says what was wrong with
      // the move, which is more useful than anything this layer could reconstruct.
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // ADD SOMEBODY THE ROSTER HAS NEVER HEARD OF, from here. The panel below offered the roster
  // and the closest matches on the filename — and on a FIRST read the roster is empty, so it said
  // "pick from the cohort below" with nothing below it. The fix lived on another screen, which is
  // the shape of telling somebody there is a problem and making them go and find where to solve
  // it. Two calls because they are two acts on two modules' tables, the same way
  // `FolderConfirm.addAndAssign` does it.
  async function addAndResolve(display_name: string) {
    const section_id = activityScope?.section_id;
    if (!section_id) return;
    setBusy(true);
    try {
      const made = await api.post<{ student_id: string }>(
        "/roster/students", { section_id, display_name });
      await resolveTo(made.student_id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  async function resolveTo(student_id: string) {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/review/${encodeURIComponent(detail.artifact_id)}/resolve`, { student_id });
      const d = await api.get<Detail>(`/review/artifact/${encodeURIComponent(detail.artifact_id)}`);
      setDetail(d);
      await changed();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveFeedback(message: string) {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/review/${encodeURIComponent(detail.artifact_id)}/feedback`, { message });
      const d = await api.get<Detail>(`/review/artifact/${encodeURIComponent(detail.artifact_id)}`);
      setDetail(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function override(ev: ScoreEvent, level: number | null, status: string, reason: string) {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/review/${encodeURIComponent(detail.artifact_id)}/override`, {
        supersedes_event_id: ev.event_id, level, status, reason,
      });
      const d = await api.get<Detail>(`/review/artifact/${encodeURIComponent(detail.artifact_id)}`);
      setDetail(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (available === false) {
    return (
      <div className="rv-empty">
        <h2>No papers yet</h2>
        <p>
          Nothing has been scored and composed into a review packet. This screen fills once the
          pipeline has run — it is not an error.
        </p>
        {error && <p className="rv-err">{error}</p>}
      </div>
    );
  }

  if (atHome) return <AssignmentHome onOpenSet={openSet} onOpenPaper={openPaper} />;

  const shown = scope ? queue.filter((r) => inScope(r, scope, byActivity)) : queue;

  return (
    <div className="rv">
      <aside className="rv-queue">
        <div className="rv-back">
          {!byActivity && <button onClick={goHome}>&larr; All assignments</button>}
          {/* THE WAY BACK TO THE CLASS. Opening a student replaces the trait profile with their
              paper, and until now nothing put it back — the title of the folder sat there looking
              like a label. It is the folder, so it opens the folder. */}
          {scopeTitle && (
            <button className="rv-scope" aria-current={!selected && !absent ? "page" : undefined}
                    onClick={() => { setSelected(null); setAbsent(null); }}>
              {scopeTitle}
            </button>
          )}
        </div>
        {/* INSIDE A COLLECTION the list is the cohort: everyone, by name, with what needs a person
            lifted into its own band above. Across every cohort it is still the queue, because
            there is no one roster to read there — a paper is the only thing those sets share. */}
        {activityId ? (
          <CollectionPeople activityId={activityId} selected={selected} reload={peopleTick}
                            selectedStudent={absent?.student_id ?? null}
                            onLoaded={setCollection}
                            onSelect={(p) => {
                              // A student with no paper is a selection too. Nothing to open, and
                              // the panel says whose name it is rather than going blank.
                              setAbsent(p.artifact_id ? null : p);
                              setSelected(p.artifact_id);
                            }} />
        ) : (
          <>
            <div className="rv-counts">
              {Object.entries(counts).map(([state, n]) => (
                <span key={state} className={`rv-chip rv-${state}`}>
                  {n} {STATE_LABEL[state] ?? state}
                </span>
              ))}
            </div>
            <ul>
              {shown.map((r) => (
                <li key={r.artifact_id}
                    className={r.artifact_id === selected ? "rv-sel" : ""}
                    onClick={() => { setAbsent(null); setSelected(r.artifact_id); }}>
                  <b>{who(r.display_name, r.student_id)}</b>
                  <span className={`rv-state rv-${r.state}`}>{STATE_LABEL[r.state] ?? r.state}</span>
                  <small>
                    {r.traits ?? 0} traits
                    {(r.needs_human ?? 0) > 0 && <em> · {r.needs_human} need you</em>}
                    {(r.holds ?? 0) > 0 && <em> · {r.holds} held</em>}
                  </small>
                </li>
              ))}
            </ul>
            {!shown.length && <p className="rv-mut">Nothing waiting.</p>}
          </>
        )}
      </aside>

      <section className="rv-paper">
        {error && <div className="rv-err">{error}</div>}
        {/* WHAT A COLLECTION OPENS ON. Not "select a paper" — the first question of a set is how
            the cohort did on it, and the trait profile is the answer. It was in the 250px column
            beside the queue, which is not a width a distribution can be read at, and it sat below
            a row of count chips that answered a smaller question first.

            It gives way to the paper as soon as one is chosen: a paper needs the room, and the
            profile is one click back. */}
        {!detail && !absent && scope?.section_id && scope?.task_id && scope?.iteration && (
          <TraitProfile sectionId={scope.section_id} taskId={scope.task_id}
                        iteration={scope.iteration}
                        title={collection
                          ? `${collection.lesson_title ?? "This lesson"} · ${collection.section_label}`
                          : scopeTitle} />
        )}
        {/* A student who handed nothing in. There is no paper to open, and the console used to
            answer a click on them with the empty state, which reads as a click that failed. */}
        {absent && (
          <div className="rv-absent">
            <h2>{absent.name}</h2>
            <p className="rv-mut">
              Nothing has been handed in to this folder.
              {absent.left_the_class && " This student has left the cohort."}
            </p>
          </div>
        )}
        {!detail && !absent && !scope && <p className="rv-mut">Select a paper.</p>}
        {detail && detail.state === "unbound"
          ? <Stuck key={detail.artifact_id} d={detail} busy={busy} onResolve={resolveTo}
                   canAdd={!!activityScope?.section_id} onAdd={addAndResolve} />
          : detail && <Paper d={detail} delivery={delivery} busy={busy} onMove={move}
                             onOverride={override} onSaveFeedback={saveFeedback} />}
      </section>
    </div>
  );
}

// A paper that exists in a folder and belongs to nobody yet. It became an artifact rather than
// being dropped — a file that exists in a folder and nowhere in the system is the failure the
// intake statuses exist to prevent — and this is where a person answers the question the matching
// could not.
//
// The candidate list may be EMPTY, and that is a real answer rather than a missing one: nothing in
// this file says whose it is. Showing three low-scoring names instead would be worse than showing
// none, because a rushed teacher accepts one and the paper is filed under a student who did not
// write it.
/** The dropdown value that means "nobody on this list". A sentinel rather than an empty string,
 *  which already means "nothing chosen". */
const ADD_NEW = "__add_new__";

function Stuck({ d, busy, onResolve, canAdd, onAdd }: {
  d: Detail; busy: boolean; onResolve: (student_id: string) => void;
  /** False when this console is showing every folder at once, where there is no one section a new
   *  student could be enrolled in. */
  canAdd: boolean;
  onAdd: (display_name: string) => void;
}) {
  const f = d.intake;
  // THE RECONCILER'S BEST GUESS, as the starting selection rather than as a row of buttons beside
  // the list. It is the same information — "this one, probably" — and one control instead of two.
  const candidates = f?.candidates ?? [];
  const [picked, setPicked] = useState(candidates[0]?.student_id ?? "");
  // THE FILE'S OWN NAME, minus its type suffix, for the teacher to edit. Not a second attempt at
  // reading a person out of a filename: `writing/intake` does that, and its note says the guess
  // must stay the reconciler's rather than become a rival one written in TypeScript. It is not
  // reachable from here — scoring may not import intake — so the honest starting point is the
  // name itself. Putting the suggestion on `intake_file` is how the two modules would share it
  // properly, and that is a migration rather than a text field.
  const fromFilename = (f?.name ?? "").replace(/\.[a-z0-9]{1,5}$/i, "").trim();
  const [newName, setNewName] = useState(fromFilename);

  return (
    <>
      <header className="rv-head">
        <div>
          <h2>{f?.name ?? d.artifact_id}</h2>
          <small>
            {f?.word_count != null ? `${f.word_count} words` : "—"}
            {f?.folder && <> · from {f.folder}</>}
          </small>
        </div>
        <span className="rv-state rv-unbound">{STATE_LABEL.unbound}</span>
      </header>

      {/* ONE BOX: the problem, and the way out of it. It used to be a statement of the problem
          ("We don't know whose paper this is" — who is *we*?), a heading asking the same thing a
          second time, a paragraph explaining why there was no shortlist, a separate row for adding
          somebody, and then the control. Five pieces of furniture around one decision. */}
      <div className="rv-holds">
        <b>Unknown student</b>
        {f?.reason_code && <small><code>{f.reason_code}</code></small>}

        <div className="rv-editbar rv-name">
          <select value={picked} disabled={busy}
                  onChange={(e) => setPicked(e.target.value)}>
            <option value="">Choose a student…</option>
            {d.roster.map((r) => (
              <option key={r.student_id} value={r.student_id}>
                {r.display_name ?? r.student_id}
              </option>
            ))}
            {/* LAST, and part of the same list, because adding somebody is one of the answers to
                "whose is this" rather than a different question asked elsewhere. On the first read
                of a folder it is the ONLY answer — the roster is empty and every other option is
                absent. */}
            {canAdd && <option value={ADD_NEW}>Add a new student</option>}
          </select>

          {picked === ADD_NEW && (
            <input id="rv-newname" value={newName} disabled={busy} autoFocus
                   aria-label="The new student's name"
                   onChange={(e) => setNewName(e.target.value)} />
          )}

          <button className="btn primary"
                  disabled={busy || !picked || (picked === ADD_NEW && !newName.trim())}
                  onClick={() => (picked === ADD_NEW ? onAdd(newName.trim()) : onResolve(picked))}>
            Save name
          </button>
        </div>

        {/* A consequence, not commentary: this is the one action here that cannot be undone. */}
        <small className="rv-mut">
          Once saved this cannot be changed — a paper that carries scores and feedback describes
          one student's writing.
        </small>
      </div>

      {f?.text && (
        <section className="rv-text">
          <h3>What it says</h3>
          <pre>{f.text}</pre>
        </section>
      )}
    </>
  );
}

function Paper({ d, delivery, busy, onMove, onOverride, onSaveFeedback }: {
  d: Detail; delivery: Delivery | null; busy: boolean;
  onMove: (s: string) => void;
  onOverride: (ev: ScoreEvent, level: number | null, status: string, reason: string) => void;
  onSaveFeedback: (message: string) => void;
}) {
  const p = d.packet;
  // ONE CELL AT A TIME. Selecting a cell in the rubric lights that criterion's passages in the
  // paper and opens the box under the table. Highlighting every criterion at once would colour
  // most of the paper and say nothing — the question a teacher has is "why did THIS get a 2", and
  // the answer is the handful of sentences that criterion was scored on.
  const [cell, setCell] = useState<CellRef | null>(null);
  const rows = useRubricRows(p?.task_id ?? d.task_id, p?.iteration ?? d.iteration,
                             p?.traits ?? [], d.resource_id);
  // A PAPER WITH NO PACKET IS STILL A PAPER. It has scores if anything scored it, and it has the
  // text intake read. The screen used to answer a click on it with a sentence and nothing else,
  // which reads as "this student handed in nothing" — the report this came from.
  if (!p) return (
    <Unscored d={d} rows={rows} cell={cell} setCell={setCell} busy={busy}
              onOverride={onOverride} />
  );

  const holds = p.feedback?.holds ?? [];
  const current = new Map(d.events.filter((e) => e.current).map((e) => [e.trait_id, e]));
  // The level the RECORD holds, which is the override's when a teacher made one — not the
  // packet's copy. The packet is what was in front of them when they decided; showing it after an
  // override would show someone their own change had not happened.
  const awarded = new Map<string, Awarded>(p.traits.map((c) => {
    const ev = current.get(c.trait_id);
    return [c.trait_id, {
      level: ev ? ev.level : c.level,
      status: ev ? ev.status : c.status,
      overridden: !!ev && ev.scorer_type !== "ai",
    }];
  }));
  const selectedRow = cell ? rows.find((r) => r.trait_id === cell.trait_id) ?? null : null;
  // Flags the rater raised beside its scores. Once the teacher has changed that score the flag
  // described a level that is no longer the record, so it goes.
  const flags = p.traits.flatMap((c) => awarded.get(c.trait_id)?.overridden ? []
    : (c.issues ?? []).map((i) => ({ ...i, trait_id: c.trait_id })));
  // Highlight the selected criterion's passages, and only while a cell is open.
  const focusCriterion = cell
    ? p.traits.find((x) => x.trait_id === cell.trait_id && (x.spans?.length ?? 0) > 0) ?? null
    : null;

  return (
    <>
      <header className="rv-head">
        <div>
          {/* The heading called `who(null, …)`, which can only ever return the identifier, so
              every paper was titled `stu-maya`. `roster` is not the place to look it up either —
              the API fills that only for an UNBOUND paper, because it is there to choose from.
              The packet now carries the name. */}
          <h2>{who(d.display_name, p.student_id ?? d.student_id)}</h2>
          <small>{p.task_id} · {p.iteration} · {p.window_label}</small>
        </div>
        <div className="rv-actions">
          <span className={`rv-state rv-${d.state}`}>{STATE_LABEL[d.state] ?? d.state}</span>
          {d.state === "in_review" && (
            <>
              <button disabled={busy} className="btn primary" onClick={() => onMove("released")}>
                Release
              </button>
              <button disabled={busy} onClick={() => onMove("withheld")}>Do not send</button>
            </>
          )}
          {d.state === "blocked" && (
            <button disabled={busy} onClick={() => onMove("in_review")}>
              Take it anyway
            </button>
          )}
        </div>
      </header>

      {holds.length > 0 && (
        <div className="rv-holds">
          <b>The drafted message was held.</b>
          <ul>{holds.map((h, i) => <li key={i}><code>{h.code}</code> {h.detail}</li>)}</ul>
          <small>
            Nothing has gone to the student. Holding costs a click; a message that goes out wrong
            cannot be clicked back.
          </small>
        </div>
      )}

      {p.prior_note && <div className="rv-note">{p.prior_note}</div>}

      {delivery?.available && delivery.attempts.length > 0 && (
        <HandBack delivery={delivery}
                  currentComposition={d.composition_id} />
      )}

      {/* THE SCORES FIRST, as the rubric they came from. A teacher reads where this paper sits
          in the grid in one glance; the prose that used to sit under every criterion is now one
          box under the table, about the cell they asked about. */}
      <section className="rv-criteria">
        <h3>Scores</h3>
        {flags.length > 0 && (
          <ul className="rv-issues" aria-label="Flagged by the rater">
            {flags.map((f) => <li key={f.trait_id + f.code}>{f.message}</li>)}
          </ul>
        )}
        <RubricTable rows={rows} awarded={awarded} selected={cell}
                     onSelect={(next) => setCell(
                       cell && cell.trait_id === next.trait_id && cell.level === next.level
                         ? null : next)} />
        {cell && selectedRow && (
          <SelectedCell row={selectedRow} cell={cell}
                        c={p.traits.find((x) => x.trait_id === cell.trait_id) ?? null}
                        ev={current.get(cell.trait_id) ?? null}
                        busy={busy} onOverride={onOverride} onClear={() => setCell(null)} />
        )}
        {!cell && <p className="rv-mut">Pick a cell to see why, and where it is in the paper.</p>}
      </section>

      {/* THE PAPER, directly under the scores. The highlights are the best thing on this screen
          and they were below everything else, which is where a teacher never got to them. */}
      {p.text && (
        <section className="rv-text">
          <h3>What they wrote</h3>
          {focusCriterion
            ? <PaperWithSpans p={p} c={focusCriterion} />
            : <pre>{p.text}</pre>}
        </section>
      )}

      {p.feedback?.message && (
        <FeedbackPanel key={d.composition_id} fb={p.feedback} busy={busy}
                       onSave={onSaveFeedback} />
      )}

      <section className="rv-stamp">
        <h3>How this was scored</h3>
        <dl>
          {Object.entries(p.stamp).map(([k, v]) => (
            <div key={k}><dt>{k.replace(/_/g, " ")}</dt><dd>{v == null ? "—" : String(v)}</dd></div>
          ))}
        </dl>
        {/* "What happened to it" — the state trail — moved out. It is an administrator's
            question about the pipeline, not a teacher's about a student, and it belongs with the
            admin help rather than under a paper somebody is marking. */}
      </section>
    </>
  );
}

// What there is of a paper that was scored and never composed: the scores, and the text.
//
// Composition is what turns scores into a review packet — the drafted message, the verified
// quotations, the held checks. When it fails, everything before it survives: score events in the
// record and the file intake read. Showing those is the difference between "this is where it got
// to" and a blank panel that reads as work never handed in.
function Unscored({ d, rows, cell, setCell, busy, onOverride }: {
  d: Detail; rows: RubricRow[]; cell: CellRef | null;
  setCell: (c: CellRef | null) => void; busy: boolean;
  onOverride: (ev: ScoreEvent, level: number | null, status: string, reason: string) => void;
}) {
  const current = new Map(d.events.filter((e) => e.current).map((e) => [e.trait_id, e]));
  const awarded = new Map<string, Awarded>(
    [...current].map(([trait_id, ev]) => [trait_id, {
      level: ev.level, status: ev.status, overridden: ev.scorer_type !== "ai",
    }]));
  const selectedRow = cell ? rows.find((r) => r.trait_id === cell.trait_id) ?? null : null;
  const text = d.intake?.text ?? null;

  return (
    <>
      <header className="rv-head">
        <div>
          <h2>{who(d.display_name, d.student_id)}</h2>
          <small>{[d.task_id, d.iteration].filter(Boolean).join(" · ")}</small>
        </div>
        <div className="rv-actions">
          <span className={`rv-state rv-${d.state}`}>{STATE_LABEL[d.state] ?? d.state}</span>
        </div>
      </header>

      <div className="rv-note">
        {current.size > 0
          ? "Scored, but the feedback was never drafted. The scores are below; nothing has been "
            + "written to the student."
          : "Handed in. Nothing has scored it yet."}
      </div>

      {rows.length > 0 && (
        <section className="rv-criteria">
          <h3>Scores</h3>
          <RubricTable rows={rows} awarded={awarded} selected={cell}
                       onSelect={(next) => setCell(
                         cell && cell.trait_id === next.trait_id && cell.level === next.level
                           ? null : next)} />
          {cell && selectedRow && (
            <SelectedCell row={selectedRow} cell={cell} c={null}
                          ev={current.get(cell.trait_id) ?? null}
                          busy={busy} onOverride={onOverride} onClear={() => setCell(null)} />
          )}
        </section>
      )}

      {text && (
        <section className="rv-text">
          <h3>What they wrote</h3>
          {/* As intake read it. There are no verified spans without a packet, so there is nothing
              to highlight and no reason to show the normalised text. */}
          <pre>{text}</pre>
        </section>
      )}
    </>
  );
}


// The rubric a paper was scored against, fetched by its task. The lesson page reads the same
// endpoint, which is the point: one instrument, one table, two places to meet it.
//
// A packet carries each criterion's label and scale but not the descriptors, so without this the
// table would be a grid of bare numbers. When the rubric cannot be read the table still renders
// from the packet — a score with no wording behind it is worth less, and worth showing.
function useRubricRows(taskId: string | null | undefined, iteration: string | null | undefined,
                       criteria: Criterion[], resourceId?: string | null): RubricRow[] {
  const [rows, setRows] = useState<RubricRow[] | null>(null);

  useEffect(() => {
    let live = true;
    setRows(null);
    if (!taskId && !resourceId) return;
    // BY LESSON WHEN THE PAPER KNOWS ITS LESSON. `/rubric?resource_id=` answers with the rubrics
    // that lesson's work is scored on, read the way scoring reads them (0112). A paper with no
    // lesson asks by task, which answers with every rubric its task is scored on.
    const query = resourceId
      ? `resource_id=${encodeURIComponent(resourceId)}`
      : `task_id=${encodeURIComponent(taskId!)}`;
    api.get<{ rubrics: { traits: RubricRow[] }[] }>(`/rubric?${query}`)
      .then((r) => {
        if (!live) return;
        // Every rubric's traits, in the lesson's order: one paper is scored on all of them.
        setRows(r.rubrics.flatMap((x) => x.traits));
      })
      .catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [taskId, iteration, resourceId]);

  // The fallback, and the shape the packet can answer in: label, scale, no wording.
  const fromPacket = criteria.map((c) => ({
    trait_id: c.trait_id,
    label: c.trait_label,
    standard: c.standard_code,
    levels: (c.scale_categories ?? []).map((level) => ({ level, descriptor: null })),
  }));
  if (!rows || rows.length === 0) return fromPacket;

  // Rubric order, but only the traits this paper was actually scored on — a rubric can list a
  // trait the scorer skipped, and a row with no score in a table about one student is a question
  // nobody asked.
  // With no packet there is nothing to narrow by, and the whole rubric is the honest answer:
  // this is what the paper was being scored against, and the empty rows are what is missing.
  if (criteria.length === 0) return rows;
  const scored = new Set(criteria.map((c) => c.trait_id));
  const known = rows.filter((r) => scored.has(r.trait_id));
  const extra = fromPacket.filter((r) => !rows.some((x) => x.trait_id === r.trait_id));
  return [...known, ...extra];
}


// The one box under the table: which cell you are looking at, what the rubric says about it, and
// — where the cell is the one the scoring chose — why it chose it.
//
// This replaces a reason and a bulleted quotation list under every criterion: six traits' worth of
// prose, most of it repeating what the highlighted passages show better. The reason is worth
// reading for ONE criterion at a time, which is the one you clicked.
function SelectedCell({ row, cell, c, ev, busy, onOverride, onClear }: {
  row: RubricRow; cell: CellRef; c: Criterion | null; ev: ScoreEvent | null;
  busy: boolean; onClear: () => void;
  onOverride: (ev: ScoreEvent, level: number | null, status: string, reason: string) => void;
}) {
  const [why, setWhy] = useState("");
  // What the MACHINE said, which is the packet's copy and never changes, and what the record
  // holds now, which is the teacher's if they have changed it.
  const suggested = c?.level ?? null;
  const standing = ev ? ev.level : suggested;
  // The cell they clicked is the score they are proposing. Clicking is how you pick a level;
  // this field is how you see what you picked and change your mind about it.
  const [mine, setMine] = useState<number | null>(cell.level);
  useEffect(() => { setMine(cell.level); }, [cell.trait_id, cell.level]);

  const level = row.levels.find((l) => l.level === cell.level);
  const isAwarded = standing === cell.level;
  const reason = ev?.reason ?? c?.reason ?? null;
  const overridden = !!ev && ev.scorer_type !== "ai";
  const changes = mine != null && mine !== standing;

  return (
    <div className="rv-cell">
      <div className="rv-cell-head">
        <b>{row.label ?? row.trait_id} · level {cell.level}</b>
        {isAwarded && <span className="rv-cell-is">this paper&rsquo;s score</span>}
        {overridden && isAwarded && <span className="rv-badge">yours</span>}
        <button className="rv-cell-x" onClick={onClear} aria-label="Close">&times;</button>
      </div>

      {/* What the rubric says this level is: the words the writing is being compared against. */}
      <p className="rv-cell-desc">
        {Array.isArray(level?.descriptor)
          ? <ul className="rt-stacked">{level!.descriptor.map((x, i) => <li key={i}>{x}</li>)}</ul>
          : level?.descriptor ?? <i className="rt-none">No wording at this level.</i>}
      </p>

      {/* The rationale belongs to the level the scoring CHOSE and to no other. Showing it under a
          level nobody picked would attach a judgment to a cell nobody made. */}
      {isAwarded && reason && <p className="rv-cell-why">{reason}</p>}
      {isAwarded && !reason && <p className="rv-mut">No reason was recorded for this one.</p>}

      {ev && (
        <div className="rv-score">
          <span className="rv-score-was">
            Suggested score <b>{suggested ?? "—"}</b>
          </span>
          <label className="rv-score-mine">
            Your score
            <input type="number" inputMode="numeric" value={mine ?? ""} disabled={busy}
                   min={row.levels[0]?.level} max={row.levels[row.levels.length - 1]?.level}
                   onChange={(e) => setMine(e.target.value === "" ? null : Number(e.target.value))} />
          </label>
          <input className="rv-score-why" value={why} placeholder="Why (optional)" disabled={busy}
                 onChange={(e) => setWhy(e.target.value)} />
          <button className="btn primary" disabled={busy || !changes}
                  onClick={() => mine != null && onOverride(ev, mine, "scored", why)}>
            Accept
          </button>
        </div>
      )}

      {c?.prior?.length ? (
        <div className="rv-prior">
          <small>Earlier on this same criterion:</small>
          {c.prior.map((pr, i) => (
            <span key={i} className={pr.same_rater ? "" : "rv-otherrater"}>
              {pr.task_id}: {pr.level}
              {!pr.same_rater && <i> different scorer</i>}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}


function PaperWithSpans({ p, c }: { p: Packet; c: Criterion }) {
  const body = p.text_normalized;
  if (!body) {
    // Scored before the offsets were kept. Say so rather than silently showing an unhighlighted
    // paper, which would read as "this criterion has no evidence in the text".
    return (
      <>
        <p className="rv-mut">
          This paper was scored before span positions were recorded, so its evidence can only be
          listed, not shown in place.
        </p>
        <pre>{p.text}</pre>
      </>
    );
  }

  const marks = [...(c.spans ?? [])]
    .filter((s) => s.at >= 0 && s.len > 0 && s.at + s.len <= body.length)
    .sort((a, b) => a.at - b.at)
    .reduce<{ at: number; end: number }[]>((acc, s) => {
      const last = acc[acc.length - 1];
      if (last && s.at <= last.end) last.end = Math.max(last.end, s.at + s.len);
      else acc.push({ at: s.at, end: s.at + s.len });
      return acc;
    }, []);

  const parts: JSX.Element[] = [];
  let cursor = 0;
  marks.forEach((m, i) => {
    if (m.at > cursor) parts.push(<span key={`t${i}`}>{body.slice(cursor, m.at)}</span>);
    parts.push(<mark key={`m${i}`}>{body.slice(m.at, m.end)}</mark>);
    cursor = m.end;
  });
  if (cursor < body.length) parts.push(<span key="tail">{body.slice(cursor)}</span>);

  return (
    <>
      {/* No "show the paper as written" button: closing the cell does that, and two ways to
          undo one thing is one more control than the screen needs. */}
      <div className="rv-spanbar">
        <b>{c.trait_label ?? c.trait_id}</b>
      </div>
      <pre className="rv-marked">{parts}</pre>
      {/* Said once, briefly. The offsets are exact in the normalised text and nowhere else, so
          highlighting means showing that text — which folds quotation marks, dashes and spacing.
          Worth knowing while reading a criterion ABOUT those things; not worth three lines. */}
      <small className="rv-mut">typography folded — the text the scoring read</small>
    </>
  );
}

// The message a student will read, in the box a teacher types in.
//
// IT IS THE POINT OF THE SCREEN. A model drafted it; the teacher's job is to make it theirs, and
// the previous version put that behind an "Edit the message" button, which made editing the
// exception and reading the default. The text is now in a textarea from the moment it renders.
//
// The heading said "Drafted for the student", which is a fact about where the words came from
// rather than what the thing is. It is feedback for the student, drafted or written.
//
// The edit APPENDS: a new composition row pointing at the one it replaces, so the machine's draft
// survives beside the version that went out.
//
// The safety checks re-run on an edit and are shown, but they do not block. The gate exists to stop
// a machine's draft reaching a person unexamined, and by the time somebody is typing here it has
// already done that. A teacher who has read the paper and written their own sentence is the
// authority; holding it would be the tool overruling them.
function FeedbackPanel({ fb, busy, onSave }: {
  fb: NonNullable<Packet["feedback"]>; busy: boolean; onSave: (m: string) => void;
}) {
  const [draft, setDraft] = useState(fb.message);
  const dirty = draft.trim() !== fb.message.trim();

  return (
    <section className="rv-feedback">
      <h3>
        Feedback for the student
        {fb.edited_by
          ? <span className="rv-badge">edited by you</span>
          : <span className="rv-mut">drafted — edit it</span>}
      </h3>

      <textarea className="rv-edit" value={draft} rows={10} disabled={busy}
                onChange={(e) => setDraft(e.target.value)} />
      <div className="rv-editbar">
        {/* Appears when there is something to save. A Save that is always there, always enabled,
            teaches a teacher to press it and find out whether anything happened. */}
        {dirty && (
          <>
            <button className="btn primary" disabled={busy} onClick={() => onSave(draft)}>
              Save the message
            </button>
            <button disabled={busy} onClick={() => setDraft(fb.message)}>Undo my changes</button>
          </>
        )}
        {!dirty && (
          <small>
            Composer {fb.composer_version} ·{" "}
            {fb.quotations.length} quotation{fb.quotations.length === 1 ? "" : "s"}, each
            verified against this student's own writing
          </small>
        )}
      </div>

      {fb.holds_are_advisory && fb.holds.length > 0 && (
        <div className="rv-note">
          <b>Worth a look, not a block.</b>
          <ul>{fb.holds.map((h, i) => <li key={i}><code>{h.code}</code> {h.detail}</li>)}</ul>
          <small>Your words, your call — this is recorded, not enforced.</small>
        </div>
      )}

      {fb.machine_draft && fb.machine_draft !== fb.message && (
        <details className="rv-original">
          <summary>What the model drafted</summary>
          <pre>{fb.machine_draft}</pre>
        </details>
      )}
    </section>
  );
}


// What happened after Release. Read only: the file channel writes where the folder is, and the API
// cannot reach it — the batch job sends until Drive makes the channel reachable from here.
//
// Three things this has to keep straight, and each of them is a different sentence on screen:
//   * what the student is HOLDING, which is the last successful send
//   * what happened LAST, which may be a failure after a success — and does not mean they have
//     nothing
//   * whether the message has been edited SINCE, in which case they are holding an older one
function HandBack({ delivery, currentComposition }: {
  delivery: Delivery; currentComposition: string;
}) {
  const { delivered, last_attempt: last, attempts } = delivery;
  const stale = !!delivered && delivered.composition_id !== currentComposition;
  const failingNow = last?.status === "failed";

  return (
    <section className="rv-criteria">
      <h3>Handed back</h3>

      {delivered ? (
        <p className={stale ? "rv-nonum" : ""}>
          {stale
            ? "The student is holding an EARLIER version of this message — it was edited after it went out."
            : "Delivered."}{" "}
          <span className="rv-mut">
            {new Date(delivered.delivered_at!).toLocaleString()} · {delivered.channel}
          </span>
        </p>
      ) : (
        <p className="rv-nonum">Nothing has reached this student yet.</p>
      )}

      {failingNow && (
        <div className="rv-holds">
          <b>The last attempt did not go.</b>
          <p>{last?.detail}</p>
          <small>
            {delivered
              ? "They still have the earlier message — this failure did not take anything away."
              : "Nothing has reached them. It will be retried."}
          </small>
        </div>
      )}

      <ol className="rv-trail">
        {attempts.map((a) => (
          <li key={a.delivery_id}>
            <b className={a.status === "sent" ? "rv-teacher" : "rv-machine"}>{a.status}</b>{" "}
            {a.attempted_at && new Date(a.attempted_at).toLocaleTimeString()}
            {a.target_ref && <> → {a.target_ref}</>}
            {a.detail && <> — {a.detail}</>}
            {/* The hash is how "which version did they read" is answerable at all. */}
            {a.message_hash && <small> · {a.message_hash.slice(0, 8)}</small>}
          </li>
        ))}
      </ol>
    </section>
  );
}
